import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import { ArrowUpRight } from 'lucide-react';
import { CAUSES } from '@/lib/site';

export default function Causes() {
  return (
    <section
      id="causes"
      data-testid="causes-section"
      className="relative py-24 md:py-32 bg-parchment/40 dark:bg-[#1F1218] overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 bg-mandala bg-center bg-no-repeat opacity-[0.08]" />

      <div className="container-x relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="eyebrow">What We Do</p>
            <h2 className="h-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-maroon-700 dark:text-foreground">
              Eight quiet revolutions,
              <br />
              <span className="italic font-serif saffron-text">one shared dharma.</span>
            </h2>
          </div>
          <p className="max-w-md text-foreground/65 leading-relaxed">
            Each cause we run is built around long-term local leadership, not parachute charity.
            Hover any program to learn how you can help.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CAUSES.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Sparkles;
            return (
              <motion.article
                key={c.id}
                data-testid={`cause-card-${c.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: 0.05 * i }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-3xl bg-card border border-border/70 shadow-sm hover:shadow-2xl transition-all"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-maroon-900/85 via-maroon-900/15 to-transparent" />
                  <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-maroon-700 backdrop-blur">
                    <Icon className="h-3.5 w-3.5 text-saffron-600" />
                    Program
                  </div>
                  <div className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-saffron-500 text-white shadow-lg opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-2 transition-all">
                    <ArrowUpRight className="h-5 w-5" />
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-saffron-600 transition-colors">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/65 line-clamp-3">
                    {c.short}
                  </p>
                  <a
                    href="#donate"
                    data-testid={`cause-learn-more-${c.id}`}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-saffron-600 dark:text-saffron-300 hover:gap-2 transition-all"
                  >
                    Learn more
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
